import { tl } from 'common/services/i18nService';
import { CustomFieldDefinitionTypes, ContentTypes } from 'common/store/common/constants';
import datePickerService from 'common/services/datePickerService';

import { capitalizeWord } from 'rubrics/src/components/rubricsTable/utils';
import * as systemFields from '../../../docs/definitions/systemFields.json';
import { CustomField, CustomFieldDataTypeList, CustomFieldDefinition } from '../../store/customFieldsDefinition/types';
import customFieldsService from './customFieldsService';
import { CustomFieldsDefinitionState } from '../../store/customFieldsDefinition/reducer';
import * as sectionDetailFields from '../../../docs/definitions/sectionDetailFields.json';

import { SectionDetailFieldType, SectionDetalFieldResponseType, ContextItem } from '../../store/context/types';

const detailsPanelService = {

    getSystemFieldsDefinition(item: any): Array<any> {

        const definitions = systemFields?.default ?? systemFields ?? []
        const dateFields = ['creationDate', 'lastModified'];
        const revisionIdFields = ['contentRevisionId', 'liveContentRevisionId'];
        const lifeCycleStatusField = 'lifeCycleStatus';
        const itemBankNameField = 'itemBankName';
        const fieldsWithDefinition = definitions.filter((definition: any) => item[definition?.ref]);

        return fieldsWithDefinition.map((definition: any) => {
            const { ref } = definition;
            let fieldValue = item[ref];

            if (dateFields.includes(ref)) {
                fieldValue = datePickerService.parseTimeFromUTCHumanized(fieldValue, 'Do MMMM YYYY, HH:mm');
            } else if (revisionIdFields.includes(ref)) {
                fieldValue = item[revisionIdFields[0]] || item[revisionIdFields[1]];
            } else if (lifeCycleStatusField === ref) {
                fieldValue = fieldValue ? capitalizeWord(fieldValue) : tl('general.notSet');
            } else if (itemBankNameField === ref) {
                fieldValue = capitalizeWord(fieldValue);
            }
            return {
                ref,
                name: tl(`common.systemFields.${ref}`),
                value: fieldValue,
            };
        });
    },

    getCustomFieldsDefinition(
        customFieldsDefinition: CustomFieldsDefinitionState,
        panelContext: CustomFieldDefinitionTypes
    ): Array<CustomFieldDefinition> {
        if (Object.values(CustomFieldDefinitionTypes).includes(panelContext)) {
            return customFieldsDefinition[panelContext].filter((field: CustomFieldDefinition) => {
                // i.e. Filtering out R&D metadata
                return field.dataType !== CustomFieldDataTypeList.OBJECT;
            });
        }
        return [];
    },

    sortFieldsByName(nameValue: string, fields: Array<any> = []): Array<any> {
        // Make sure the fields are alphabetical order before sorting with column order number
        const alphabeticallySortedFields = [...fields];
        alphabeticallySortedFields.sort((a: any, b: any) => {
            const nameA = a[nameValue].toUpperCase();
            const nameB = b[nameValue].toUpperCase();

            if (nameA > nameB) return 1;
            if (nameA < nameB) return -1;
            return 0;
        });

        // Sort by columnOrderNumber
        const columnSortedFields = [...alphabeticallySortedFields];
        columnSortedFields.sort((a: any, b: any) => {
            if (a.columnOrderNumber > b.columnOrderNumber) return 1;
            if (a.columnOrderNumber < b.columnOrderNumber) return -1;

            return 0;
        });

        return columnSortedFields;
    },

    isSystemFieldHit(field: any, search: string): boolean {
        const { name, value } = field;
        if (search) {
            return (
                name.toLowerCase().includes(search.toLowerCase())
                || value?.toString().toLowerCase().includes(search.toLowerCase())
            );
        }
        return true;
    },

    isCustomFieldHit(field: CustomFieldDefinition, search: string, customFields: Array<any>): boolean {
        const { fieldName, fieldDefinitionId } = field;
        if (search) {
            const nameHit = fieldName.toLowerCase().includes(search.toLowerCase());
            const value = customFieldsService.getValueFromCustomField(
                customFields.find((field: CustomField) => field.customFieldDefinitionId === fieldDefinitionId)
            ) || '';
            const valueHit = Array.isArray(value)
                ? value.some((val: any) => val.toString().toLowerCase().includes(search.toLowerCase()))
                : value.toString().toLowerCase().includes(search.toLowerCase());

            return nameHit || valueHit;
        }
        return true;
    },

    getSectionDetailFieldsDefinition(item: ContextItem, questionMetaData: any): Array<Record<SectionDetailFieldType, SectionDetalFieldResponseType>> {
        const detailSectionFields: Array<Record<SectionDetailFieldType, string>> = this.getSectionDetailFields(item.contextContentType);
        return detailSectionFields.map((definition: Record<SectionDetailFieldType, string>) => {
            const { ref, type } = definition;
            let fieldValue = item[ref as keyof typeof item] ?? (questionMetaData?.[ref]);
            const arrayJoinFields = ['labels'];
            if (arrayJoinFields.includes(ref) && fieldValue) {
                fieldValue = fieldValue.split('\\n').slice(1).join(', ');
            }

            return {
                ref,
                name: tl(`common.systemFields.${ref}`),
                value: fieldValue,
                type,
            };
        });
    },

    getSectionDetailFields(contentType: string): Array<Record<SectionDetailFieldType, string>> {
        switch (contentType) {
        case ContentTypes.SECTION:
            return sectionDetailFields?.default ?? sectionDetailFields ?? []
        case ContentTypes.QUESTIONS:
        case ContentTypes.QUESTION_SET:
            return [];
        default:
            return [];
        }
    },

    onSaveCustomFields(customFieldsDefinitionKit: any[], customFieldDefinitions: any[]): any {
        const requestData = customFieldsDefinitionKit
            .filter(({ isDirty }: any) => isDirty)
            .map((item: any) => {
                let filteredValues: string[] = [];
                const definition = customFieldDefinitions.find(
                    (def: CustomFieldDefinition) => def.fieldDefinitionId === item.fieldDefinitionId
                );
                if (definition) {
                    let { allowedValues } = definition;
                    if (allowedValues?.every((val: any) => typeof val === 'object')) {
                        allowedValues = allowedValues?.map((value: any) => value.allowedValue);
                    }

                    if (allowedValues?.length > 0 && Array.isArray(item.value) && item.value.every((val: any) => typeof val === 'string')) {
                        filteredValues = item.value.filter((val: string) => allowedValues.includes(val));
                    } else {
                        filteredValues = item.value;
                    }
                } else {
                    filteredValues = item.value;
                }

                if (filteredValues === null) {
                    filteredValues = [];
                } else if (!Array.isArray(filteredValues)) {
                    filteredValues = [item.value];
                }

                return {
                    customFieldDefinitionId: item.fieldDefinitionId,
                    properties: item.properties,
                    values: filteredValues ?? [],
                };
            });
        return requestData;
    },
};

export default detailsPanelService;
